import PropTypes from 'prop-types';
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import connect from 'components/lib/connect';
import MenuIcon from 'components/lib/icons/menu_icon';
import OpenModal from 'actions/modal/open_modal';
import PopoverMenu, { PopoverMenuItem, usePopoverMenu } from 'components/common/menu';
import ProfileAvatar from 'components/lib/profile_avatar';
import RedactedItem from 'components/customer/conversation_history/conversation_items_v2/redacted_item';
import RedactTaskComment from 'models/modal/redact_task_comment';
import RelativeDateTime from 'components/lib/date/relative_date_time';
import SafeHtml from 'components/common/safe_html';
import SetSelectedCommentId from 'actions/task/set_selected_comment_id';
import TimestampFormatter from 'components/lib/date/lib/timestamp_formatter';
import Tooltip from 'components/common/tooltip';
import { useExecuteAction } from 'components/hooks/connect_hooks';
import useWindowSize from 'components/hooks/use_window_size';

export function TaskComment({
  agentAvatarUrl,
  agentName,
  canRedactComment,
  className,
  createdAt,
  currentAgentId,
  id,
  isRedacted,
  itemId,
  message,
  selectedAt,
}) {
  const ref = useRef(null);
  const executeAction = useExecuteAction();
  const [highlighted, setHighlighted] = useState(false);
  const { targetRef, setTargetRef, isOpen, onClose, onToggle } = usePopoverMenu();

  // If this item becomes selected, scroll to it, then unselect it (it's just a momentary thing so we can
  // scroll to it!)
  useLayoutEffect(() => {
    if (selectedAt) {
      ref.current.scrollIntoView({
        block: 'center',
      });
      executeAction(SetSelectedCommentId, { id: null });
      setHighlighted(true);
    }
  }, [executeAction, selectedAt]);

  // Remove the highlight after the CSS transition completes
  useEffect(() => {
    if (highlighted) {
      const timer = setTimeout(() => setHighlighted(false), 2000);
      return () => clearTimeout(timer);
    }
  }, [highlighted]);

  function renderAvatar() {
    return (
      <StyledAvatar data-aid="taskComment-avatar" noWrapper profile={{ name: agentName, image: agentAvatarUrl }} />
    );
  }

  function renderDetail() {
    return (
      <StyledCommentDetail $currentAgentId={currentAgentId}>
        <StyledNameRow>
          <StyledName data-aid="taskComment-content-name">
            <bdi>{agentName || 'Agent'}</bdi>
          </StyledName>
          <StyledNameRowActions>
            <Date timestamp={createdAt} />
            {canRedactComment && renderMenuButton()}
          </StyledNameRowActions>
        </StyledNameRow>
        {isRedacted ? (
          <RedactedItem />
        ) : (
          <bdi>
            <StyledMessage data-aid="taskComment-content-message" html={message} setDirection />
          </bdi>
        )}
        {renderMenu()}
      </StyledCommentDetail>
    );
  }

  function renderMenuButton() {
    return (
      <StyledMenuButton
        aria-expanded={isOpen}
        aria-haspopup="menu"
        data-aid="taskComment-menuButton"
        onClick={onToggle}
        ref={setTargetRef}
        type="button"
      >
        <MenuIcon />
      </StyledMenuButton>
    );
  }

  function renderMenu() {
    return (
      <StyledMenu
        data-aid="taskComment-menu"
        isOpen={isOpen}
        onClose={onClose}
        position="bottom"
        targetPosition="end"
        targetRef={targetRef}
      >
        {canRedactComment && (
          <StyledDeleteMenuItem
            data-aid="taskComment-deleteComment"
            onClick={() => executeAction(OpenModal, new RedactTaskComment({ itemId, commentId: id }))}
          >
            Delete Comment
          </StyledDeleteMenuItem>
        )}
      </StyledMenu>
    );
  }

  return (
    <StyledComment $highlighted={highlighted} className={className} data-aid="taskComment" data-id={id} ref={ref}>
      {renderAvatar()}
      {renderDetail()}
    </StyledComment>
  );
}

TaskComment.propTypes = {
  agentAvatarUrl: PropTypes.string,
  agentName: PropTypes.string,
  canRedactComment: PropTypes.bool,
  className: PropTypes.string,
  createdAt: PropTypes.string,
  currentAgentId: PropTypes.string,
  id: PropTypes.string,
  isRedacted: PropTypes.bool,
  itemId: PropTypes.string,
  message: PropTypes.string,
  selectedAt: PropTypes.string,
};

function mapStateToProps({ getProvider }, { initiatingAgentId }) {
  const agentsProvider = getProvider('agents');
  const agent = agentsProvider.findBy({ id: initiatingAgentId });

  return {
    agentName: agent ? agent.getDisplayName() : 'An agent',
    agentAvatarUrl: agent ? agent.avatarUrl : '',
    currentAgentId: getProvider('currentAgent').get().id,
  };
}

const TaskCommentContainer = connect(mapStateToProps)(TaskComment);

TaskCommentContainer.propTypes = {
  initiatingAgentId: PropTypes.string.isRequired,
};

export const StyledAvatar = styled(ProfileAvatar)`
  height: 35px;
  width: 35px;
`;

const StyledName = styled.div`
  font-weight: 600;
`;

const StyledCommentDetail = styled.div`
  flex-grow: 1;
  margin-left: ${p => p.theme.spacing.medium};

  agentmention {
    background-color: ${p => p.theme.colors.gray100};
    border-radius: 4px;
    color: ${p => p.theme.colors.green400};
    font-weight: bold;
    padding: 4px;

    &:hover {
      background-color: ${p => p.theme.colors.gray200};
    }

    &[data-agentid='${p => p.$currentAgentId}'],
    &[data-agentid='${p => p.$currentAgentId}']:hover {
      background-color: ${p => p.theme.colors.yellow200};
    }
  }
`;

const StyledMenuButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  display: none;
  fill: ${p => p.theme.colors.gray600};
  margin-left: 8px;
  padding: 0;

  &:hover {
    fill: ${p => p.theme.colors.gray700};
  }

  svg {
    height: 14px;
  }
`;

const StyledNameRow = styled.div`
  display: flex;
  justify-content: space-between;
`;

const StyledNameRowActions = styled.div`
  align-items: center;
  display: flex;
`;

const StyledDate = styled(RelativeDateTime)`
  color: ${p => p.theme.colors.gray600};
  cursor: pointer;
  display: none;
  margin-left: 8px;

  &:hover {
    color: ${p => p.theme.colors.gray700};
    text-decoration: underline;
  }
`;

export const StyledMessage = styled(SafeHtml)`
  word-break: break-word;

  ol,
  ul {
    margin: 4px 0;
    padding-inline-start: 16px;
  }
`;

const StyledComment = styled.div`
  background-color: ${p => (p.$highlighted ? p.theme.colors.green100 : p.theme.colors.white)};
  display: flex;
  padding: ${p => p.theme.spacing.medium} 16px;
  transition: background-color 2s ease;

  &:hover {
    ${StyledDate} {
      display: block;
    }
    ${StyledMenuButton} {
      display: block;
    }
  }
`;

const StyledMenu = styled(PopoverMenu)`
  width: auto;
  min-width: 140px;
`;

const StyledDeleteMenuItem = styled(PopoverMenuItem)`
  color: ${p => p.theme.colors.red400};
  &:hover {
    background: ${p => p.theme.colors.red400};
    color: ${p => p.theme.colors.white};
  }
`;

const Date = React.memo(function ({ timestamp }) {
  let tf = new TimestampFormatter(timestamp);
  let fullTime = tf.full();

  const { windowHeight, windowWidth } = useWindowSize();
  const bounds = {
    bottom: windowHeight - 68,
    left: 8,
    right: windowWidth - 8,
    top: 68,
  };

  return (
    <Tooltip
      bounds={bounds}
      delay={300}
      hoverableTooltip
      margin={5}
      message={<span>{fullTime}</span>}
      position="top"
      style={{ padding: '4px 8px' }}
    >
      <StyledDate data-aid="taskComment-content-timestamp" timestamp={timestamp} title="" />
    </Tooltip>
  );
});

export default TaskCommentContainer;
